import { useState, useCallback, useEffect } from 'react';
import { Notification, NotificationQueryParams } from '../../types/notification';
import { notificationAPI } from '../../services';
import { useApiCall } from '../common/useApiCall';
import { API_CONFIG } from '../../config/constants';

export type UserType = 'teacher' | 'user';

interface UseNotificationsOptions {
  autoLoad?: boolean;
  limit?: number;
  enabled?: boolean; // 훅 활성화 여부 (false면 API 호출 안함)
}

interface UseNotificationsReturn {
  notifications: Notification[];
  loading: boolean;
  error: string | null;
  loadNotifications: () => Promise<void>;
  markAsRead: (id: number) => Promise<void>;
  refresh: () => Promise<void>;
  unreadCount: number;
}

/**
 * 알림 관리를 위한 커스텀 훅
 *
 * @param userType 사용자 타입 ('teacher' | 'user')
 * @param options 옵션 (autoLoad, limit)
 * @returns 알림 목록, 로딩 상태, 에러, 관련 함수들
 *
 * @example
 * const {
 *   notifications,
 *   loading,
 *   loadNotifications,
 *   markAsRead,
 *   refresh
 * } = useNotifications('teacher');
 */
export const useNotifications = (
  userType: UserType,
  options: UseNotificationsOptions = {}
): UseNotificationsReturn => {
  const {
    autoLoad = true,
    limit = API_CONFIG.DEFAULT_PAGE_SIZE,
    enabled = true
  } = options;

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const api = useApiCall();

  /**
   * 읽지 않은 알림 개수 계산
   */
  const unreadCount = notifications.filter(n => !n.isRead).length;

  /**
   * 알림 목록 로드
   */
  const loadNotifications = useCallback(async () => {
    const apiCall = userType === 'teacher'
      ? notificationAPI.getMyForTeacher
      : notificationAPI.getMyForUser;

    const params: NotificationQueryParams = { limit };

    const data = await api.execute(
      () => apiCall(params),
      {
        errorMessage: '알림을 불러올 수 없습니다',
        showErrorToast: true,
        showSuccessToast: false
      }
    );

    if (data) {
      // API 응답 구조에 따라 처리
      const list = data?.data?.notifications || data?.notifications || [];
      setNotifications(list);
    }
  }, [userType, limit]);

  /**
   * 알림을 읽음 처리
   */
  const markAsRead = useCallback(async (id: number) => {
    const markApi = userType === 'teacher'
      ? notificationAPI.markReadTeacher
      : notificationAPI.markReadUser;

    const data = await api.execute(
      () => markApi(id),
      {
        showErrorToast: true,
        showSuccessToast: false,
        onSuccess: () => {
          // 로컬 상태 업데이트
          setNotifications(prev =>
            prev.map(n => n.id === id ? { ...n, isRead: true } : n)
          );
        }
      }
    );

    return data;
  }, [userType]);

  /**
   * 알림 새로고침
   */
  const refresh = useCallback(async () => {
    await loadNotifications();
  }, [loadNotifications]);

  /**
   * 컴포넌트 마운트 시 자동 로드 (enabled가 true일 때만)
   */
  useEffect(() => {
    if (autoLoad && enabled) {
      loadNotifications();
    }
  }, [autoLoad, enabled, loadNotifications]);

  return {
    notifications,
    loading: api.loading,
    error: api.error,
    loadNotifications,
    markAsRead,
    refresh,
    unreadCount
  };
};
